import { useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../api/client';

/**
 * EventClosedGate — when the backend reports EVENT_CLOSED, show a friendly
 * "concluded" landing page to everyone below Organizer. Organizer+ users (and
 * the /login route) pass straight through so the planning team keeps working.
 *
 * Reversible: driven entirely by the server's /public/event-status flag.
 */
export function EventClosedGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [closed, setClosed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/public/event-status`)
      .then((r) => (r.ok ? r.json() : { closed: false }))
      .then((d) => { if (!cancelled) setClosed(Boolean(d?.closed)); })
      .catch(() => { if (!cancelled) setClosed(false); });
    return () => { cancelled = true; };
  }, []);

  const isOrganizerPlus = user?.role === 'Organizer' || user?.role === 'Admin';
  // Always let people reach the login page so organizers can sign in.
  const onLogin = location.pathname.startsWith('/login');

  // While we don't yet know, render the app (avoids a flash of the gate).
  if (closed && !isOrganizerPlus && !onLogin) {
    return <ConcludedPage />;
  }

  return <>{children}</>;
}

function ConcludedPage() {
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.emoji}>🌴</div>
        <h1 style={styles.title}>KDU Georgia 2026 has concluded</h1>
        <p style={styles.thanks}>Thank you for an unforgettable reunion weekend.</p>
        <div style={styles.divider} />
        <p style={styles.next}>
          See you at <strong>KDU California 2027</strong>.
        </p>
        <a href="/login" style={styles.loginLink}>Organizer sign in →</a>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    position: 'fixed', inset: 0,
    background: 'linear-gradient(135deg, #0b1f16 0%, #1a6b3c 100%)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1.5rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    maxWidth: '520px', width: '100%', textAlign: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '20px', padding: '2.5rem 2rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  emoji: { fontSize: '3rem', marginBottom: '0.5rem' },
  title: { fontSize: '1.7rem', fontWeight: 900, margin: '0 0 0.5rem' },
  thanks: { fontSize: '1.05rem', color: '#dcfce7', margin: 0 },
  divider: { height: '1px', backgroundColor: 'rgba(255,255,255,0.2)', margin: '1.5rem 0' },
  next: { fontSize: '1.2rem', color: '#fff', margin: '0 0 1.5rem' },
  loginLink: { color: '#a7f3d0', fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem' },
};
